import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  Radio,
  Mic,
  Sparkles,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Timer,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STREAM_URL = `${BACKEND_URL}/api/stream/kioo`;

const AUTO_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const confBadge = (c) => {
  if (c === 'high') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (c === 'medium') return 'bg-amber-100 text-amber-800 border-amber-200';
  return 'bg-gray-100 text-gray-700 border-gray-200';
};

const KissiLearn = () => {
  const [phrases, setPhrases] = useState([]);
  const [total, setTotal] = useState(0);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [lastCapture, setLastCapture] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('recent');
  const [autoLearn, setAutoLearn] = useState(false);
  const [showStudy, setShowStudy] = useState(false);
  const [isStreamPlaying, setIsStreamPlaying] = useState(false);
  const audioRef = useRef(null);
  const autoTimerRef = useRef(null);

  const loadPhrases = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ sort, limit: '200' });
      if (q.trim()) params.set('q', q.trim());
      const { data } = await axios.get(`${API}/kissi/phrases?${params.toString()}`);
      setPhrases(data.phrases || []);
      setTotal(data.total || 0);
      setVerifiedCount(data.verified_count || 0);
    } catch (e) {
      setError('Unable to load Kissi phrases.');
    } finally {
      setLoading(false);
    }
  };

  const runCapture = async () => {
    if (capturing) return;
    setCapturing(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/kissi/capture?seconds=30`);
      setLastCapture(data);
      await loadPhrases();
    } catch (e) {
      const msg =
        e?.response?.data?.detail || 'Capture failed. Is the stream reachable?';
      setError(String(msg));
    } finally {
      setCapturing(false);
    }
  };

  const toggleStream = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (isStreamPlaying) {
      el.pause();
      setIsStreamPlaying(false);
    } else {
      try {
        el.src = STREAM_URL; // reset src every time we start
        await el.play();
        setIsStreamPlaying(true);
      } catch (_e) {
        setError('Browser blocked autoplay — click Play again to start.');
      }
    }
  };

  const verifyPhrase = async (id, english_meaning) => {
    try {
      await axios.post(`${API}/kissi/phrases/${id}/verify`, {
        english_meaning,
        verified: true,
      });
      await loadPhrases();
    } catch (_e) {
      setError('Could not verify phrase.');
    }
  };

  const deletePhrase = async (id) => {
    if (!window.confirm('Delete this phrase from the library?')) return;
    try {
      await axios.delete(`${API}/kissi/phrases/${id}`);
      await loadPhrases();
    } catch (_e) {
      setError('Could not delete phrase.');
    }
  };

  // initial load + when sort/q change
  useEffect(() => {
    loadPhrases();
  }, [sort]);

  // Auto-learn timer
  useEffect(() => {
    if (autoLearn) {
      autoTimerRef.current = setInterval(() => {
        runCapture();
      }, AUTO_INTERVAL_MS);
    } else if (autoTimerRef.current) {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    }
    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, [autoLearn]);

  const stats = useMemo(
    () => ({
      total,
      verified: verifiedCount,
      pct:
        total > 0 ? Math.round((verifiedCount / total) * 100) : 0,
    }),
    [total, verifiedCount]
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-amber-50 pb-20">
      <Helmet>
        <title>Learn Kissi | Kioo Radio 98.1FM</title>
        <meta
          name="description"
          content="Kioo Radio's smart Kissi language learner — listen live and build a growing library of Kissi phrases with English meanings."
        />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-600 text-white py-14 md:py-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-emerald-100 text-sm mb-4">
            <Sparkles className="w-4 h-4" />
            AI-powered • Learns from live broadcasts
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3">
            Learn Kissi with Kioo Radio
          </h1>
          <p className="text-lg md:text-xl text-green-100 max-w-3xl leading-relaxed">
            Tune in to Kioo Radio 98.1FM and let the AI listener capture Kissi
            phrases as they air. Every capture grows a living dictionary that
            will one day help non-Kissi speakers learn the language of the
            Makona River Region.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 -mt-8">
        {/* Live Stream Card */}
        <div
          data-testid="kissi-live-card"
          className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5 md:p-6 flex flex-col md:flex-row gap-5 items-start md:items-center"
        >
          <div className="flex items-center gap-4 flex-1">
            <div
              className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white ${
                isStreamPlaying
                  ? 'bg-red-500 animate-pulse'
                  : 'bg-emerald-600'
              }`}
            >
              <Radio className="w-7 h-7" />
            </div>
            <div>
              <div className="text-sm font-semibold text-emerald-700">
                {isStreamPlaying ? '🔴 LIVE' : '📻 READY TO LISTEN'}
              </div>
              <div className="text-lg font-bold text-gray-900">
                Kioo Radio 98.1FM
              </div>
              <div className="text-xs text-gray-500">
                Broadcasting in Kissi, English, French, Krio &amp; Mende
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <button
              data-testid="kissi-stream-toggle"
              onClick={toggleStream}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold transition ${
                isStreamPlaying
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              {isStreamPlaying ? 'Pause Stream' : 'Play Stream'}
            </button>
            <button
              data-testid="kissi-capture-btn"
              onClick={runCapture}
              disabled={capturing}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white transition"
            >
              {capturing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Listening…
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4" />
                  Capture 30s &amp; Learn
                </>
              )}
            </button>
            <label
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-medium bg-gray-100 hover:bg-gray-200 cursor-pointer text-sm text-gray-800"
              data-testid="kissi-auto-toggle-label"
            >
              <Timer className="w-4 h-4" />
              <input
                data-testid="kissi-auto-toggle"
                type="checkbox"
                checked={autoLearn}
                onChange={(e) => setAutoLearn(e.target.checked)}
                className="accent-emerald-600"
              />
              Auto-learn every 5 min
            </label>
          </div>

          {/* Hidden audio element for stream playback on this page */}
          <audio
            ref={audioRef}
            preload="none"
            onPlay={() => setIsStreamPlaying(true)}
            onPause={() => setIsStreamPlaying(false)}
            onError={() => setIsStreamPlaying(false)}
            style={{ display: 'none' }}
          />
        </div>

        {/* Stats + last capture */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <StatCard label="Phrases Learned" value={stats.total} testid="kissi-stat-total" />
          <StatCard
            label="Verified"
            value={`${stats.verified} (${stats.pct}%)`}
            testid="kissi-stat-verified"
          />
          <StatCard
            label="Last Capture"
            value={lastCapture ? `${lastCapture.phrases_found} phrase(s)` : '—'}
            testid="kissi-stat-last"
          />
          <StatCard
            label="Detected Lang"
            value={lastCapture?.detected_language || '—'}
            testid="kissi-stat-lang"
          />
        </div>

        {error && (
          <div
            data-testid="kissi-error"
            className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm"
          >
            {error}
          </div>
        )}

        {lastCapture?.transcript_preview && (
          <div
            data-testid="kissi-transcript-preview"
            className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Raw transcript (last capture)
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">
              {lastCapture.transcript_preview}
            </p>
          </div>
        )}

        {/* Controls */}
        <div className="mt-8 flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="kissi-search"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadPhrases();
                }}
                placeholder="Search Kissi phrases or English meaning…"
                className="w-full pl-9 pr-3 py-2 rounded-full border border-gray-300 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <button
              data-testid="kissi-search-btn"
              onClick={loadPhrases}
              className="px-4 py-2 rounded-full bg-gray-800 text-white text-sm font-medium hover:bg-gray-900"
            >
              Search
            </button>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="kissi-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-3 py-2 rounded-full border border-gray-300 text-sm bg-white"
            >
              <option value="recent">Most recent</option>
              <option value="top">Most heard</option>
              <option value="verified">Verified first</option>
            </select>
            <button
              data-testid="kissi-open-study"
              onClick={() => setShowStudy(true)}
              disabled={phrases.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              <BookOpen className="w-4 h-4" />
              Study Mode
            </button>
          </div>
        </div>

        {/* Phrase library */}
        <div className="mt-6">
          {loading && (
            <div data-testid="kissi-loading" className="text-center py-10 text-gray-500">
              Loading library…
            </div>
          )}
          {!loading && phrases.length === 0 && (
            <div
              data-testid="kissi-empty"
              className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl"
            >
              <div className="text-5xl mb-3">📖</div>
              <h3 className="text-lg font-bold text-gray-800 mb-1">
                No phrases yet
              </h3>
              <p className="text-sm text-gray-600">
                Press <span className="font-semibold">“Capture 30s &amp; Learn”</span>{' '}
                while Kioo Radio is playing to start the library.
              </p>
            </div>
          )}
          {!loading && phrases.length > 0 && (
            <div
              data-testid="kissi-phrase-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {phrases.map((p) => (
                <PhraseCard
                  key={p.id}
                  phrase={p}
                  onVerify={verifyPhrase}
                  onDelete={deletePhrase}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {showStudy && (
        <StudyMode phrases={phrases} onClose={() => setShowStudy(false)} />
      )}
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div
    data-testid={testid}
    className="bg-white rounded-xl border border-gray-100 shadow-sm p-4"
  >
    <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
      {label}
    </div>
    <div className="mt-1 text-xl font-bold text-gray-900 break-words">
      {value}
    </div>
  </div>
);

const PhraseCard = ({ phrase, onVerify, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [meaning, setMeaning] = useState(phrase.english_meaning || '');

  return (
    <div
      data-testid={`kissi-phrase-${phrase.id}`}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition p-4 flex flex-col"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-lg font-bold text-gray-900 break-words">
            {phrase.kissi_text}
          </div>
          <div className="mt-1 text-sm text-emerald-800 break-words">
            {phrase.english_meaning || <em className="text-gray-400">(no translation yet)</em>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${confBadge(
              phrase.confidence
            )}`}
          >
            {phrase.confidence || 'low'}
          </span>
          {phrase.verified && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
              <ShieldCheck className="w-3 h-3" /> verified
            </span>
          )}
        </div>
      </div>

      {phrase.context && (
        <div className="mt-3 text-xs text-gray-500 italic break-words">
          “{phrase.context}”
        </div>
      )}

      {(phrase.example_sentence || phrase.example_translation) && (
        <div className="mt-3 p-2 rounded-lg bg-emerald-50/60 text-xs text-emerald-900 border border-emerald-100">
          {phrase.example_sentence && (
            <div className="font-medium">“{phrase.example_sentence}”</div>
          )}
          {phrase.example_translation && (
            <div className="opacity-80">— {phrase.example_translation}</div>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>heard {phrase.times_seen}× · {phrase.part_of_speech}</span>
        <div className="flex gap-1">
          <button
            data-testid={`kissi-verify-${phrase.id}`}
            onClick={() => setEditing((v) => !v)}
            className="p-1.5 rounded-full hover:bg-emerald-100 text-emerald-700"
            title="Verify / edit meaning"
          >
            <ShieldCheck className="w-4 h-4" />
          </button>
          <button
            data-testid={`kissi-delete-${phrase.id}`}
            onClick={() => onDelete(phrase.id)}
            className="p-1.5 rounded-full hover:bg-red-100 text-red-600"
            title="Delete phrase"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <label className="text-xs font-semibold text-gray-700">
            English meaning
          </label>
          <textarea
            data-testid={`kissi-edit-meaning-${phrase.id}`}
            value={meaning}
            onChange={(e) => setMeaning(e.target.value)}
            rows={2}
            className="mt-1 w-full text-sm border border-gray-300 rounded-lg p-2 focus:outline-none focus:border-emerald-500"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              onClick={() => {
                setMeaning(phrase.english_meaning || '');
                setEditing(false);
              }}
              className="text-xs px-3 py-1 rounded-full bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              data-testid={`kissi-save-verify-${phrase.id}`}
              onClick={() => {
                onVerify(phrase.id, meaning);
                setEditing(false);
              }}
              className="text-xs px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              Save &amp; Verify
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const StudyMode = ({ phrases, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [reveal, setReveal] = useState(false);
  const safeIdx = Math.max(0, Math.min(idx, phrases.length - 1));
  const p = phrases[safeIdx];
  if (!p) return null;
  const next = () => {
    setReveal(false);
    setIdx((i) => (i + 1) % phrases.length);
  };
  const prev = () => {
    setReveal(false);
    setIdx((i) => (i - 1 + phrases.length) % phrases.length);
  };

  return (
    <div
      data-testid="kissi-study-modal"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white">
          <h3 className="font-bold text-lg">Kissi Flashcards</h3>
          <button
            onClick={onClose}
            data-testid="kissi-study-close"
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <div className="p-8 text-center min-h-[340px] flex flex-col items-center justify-center">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            {safeIdx + 1} / {phrases.length} · heard {p.times_seen}×
          </div>
          <div className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 break-words">
            {p.kissi_text}
          </div>

          {reveal ? (
            <>
              <div
                data-testid="kissi-study-reveal"
                className="text-xl md:text-2xl font-semibold text-emerald-700 mb-2 break-words"
              >
                {p.english_meaning || '(no translation yet)'}
              </div>
              {p.context && (
                <div className="text-xs text-gray-500 italic max-w-md">
                  “{p.context}”
                </div>
              )}
            </>
          ) : (
            <button
              data-testid="kissi-study-reveal-btn"
              onClick={() => setReveal(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              <Eye className="w-4 h-4" />
              Show meaning
            </button>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <button
            data-testid="kissi-study-prev"
            onClick={prev}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-sm font-medium"
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button
            onClick={() => setReveal((r) => !r)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-sm font-medium"
          >
            {reveal ? (
              <>
                <EyeOff className="w-4 h-4" /> Hide
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" /> Show
              </>
            )}
          </button>
          <button
            data-testid="kissi-study-next"
            onClick={next}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default KissiLearn;
